import { Building2, Car, FileText, History, ScrollText, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import {
	DelayedSkeleton,
	DetailsWithListsSkeleton
} from '@/components/ui/delayed-skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { StatusBadgeDisplay } from '@/components/ui/status-badge-display';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { CustomerDisplayItem } from '@/components/ui/customer-display-item';
import { CustomerAddressDisplayItem } from '@/components/ui/customer-address-display-item';
import { CustomerContactDisplayItem } from '@/components/ui/customer-contact-display-item';
import { VehicleDisplayItem, VehicleDisplayList } from '@/components/ui/vehicle-display-item';
import { ParkingSessionsListPanel } from '@/pages/parking-sessions/parking-sessions-list-panel';
import { useOne } from '@/hooks/resourceQueries';

import './customers.gql.tsx';
import {
	DetailFieldsGrid,
	DetailSection,
	DetailField
} from '@/components/ui/details-panel.tsx';
import { CustomerContractsTab } from './customer-contracts-tab';


interface CustomerCategorySummary {
	customerCategoryId: string;
	description?: string | null;
}

interface CustomerContactRecord {
	customerContactId: string;
	contactTypeId: number;
	contactValue?: string | null;
	label?: string | null;
	isPrimary: boolean;
	isDeleted: boolean;
	isVerified: boolean;
	verifiedAt?: string | null;
	notes?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	contactType?: {
		contactTypeId: number;
		name: string;
		isActive: boolean;
	} | null;
}

interface CustomerAddressRecord {
	customerAddressId: string;
	addressId: string;
	addressTypeId?: number | null;
	addressType?: {
		name?: string | null;
	} | null;
	address?: {
		streetAddress?: string | null;
		city?: string | null;
		state?: string | null;
		postalCode?: string | null;
		country?: string | null;
	} | null;
}

interface CustomerBankDetailRecord {
	bankDetailsId: string;
	bankName?: string | null;
	accountNumber?: string | null;
	accountType?: string | null;
	iban?: string | null;
	bankCode?: string | null;
	branchCode?: string | null;
	branchName?: string | null;
	accountHolderName?: string | null;
	currency?: string | null;
	purpose?: string | null;
	notes?: string | null;
	isPrimary: boolean;
	isActive: boolean;
	isDeleted: boolean;
	isVerified: boolean;
	verificationMethod?: string | null;
	verifiedAt?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

interface CustomerBeneficiaryRecord {
	customerBeneficiaryId: string;
	beneficiaryId: string;
	isActive: boolean;
	notes?: string | null;
	relationshipType?: string | null;
	isDeleted: boolean;
	beneficiary?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		displayName?: string | null;
		isCompany?: boolean;
	} | null;
}

interface CustomerVehicleRecord {
	vehicleId: string;
	plateNumber?: string | null;
	make?: string | null;
	model?: string | null;
	color?: string | null;
	isActive: boolean;
	vehicleTypeId?: number | null;
	vehicleType?: {
		vehicleTypeId: number;
		name?: string | null;
	} | null;
}

interface CustomerRecord {
	customerId: string;
	isCompany: boolean;
	firmName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	fiscalNumber?: string | null;
	taxOffice?: string | null;
	billingLegalName?: string | null;
	billingTradeName?: string | null;
	billingCountryCode?: string | null;
	marketingOptIn: boolean;
	notes?: string | null;
	isActive: boolean;
	customerCategoryId: string;
	customerCategory?: CustomerCategorySummary | null;
	preferredLanguage?: string | null;
	companyRegistrationNumber?: string | null;
	industry?: string | null;
	companySize?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	activatedAt?: string | null;
	deactivatedAt?: string | null;
	dateOfBirth?: string | null;
	gender?: string | null;
	idNumber?: string | null;
	idType?: string | null;
	nationality?: string | null;
	customerContacts?: CustomerContactRecord[] | null;
	customerAddresses?: CustomerAddressRecord[] | null;
	bankDetails?: CustomerBankDetailRecord[] | null;
	customerBeneficiaries?: CustomerBeneficiaryRecord[] | null;
	vehicles?: CustomerVehicleRecord[] | null;
}

interface CustomerDetailsProps {
	customerId: string;
	// Header props for modal display
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

export const CustomerEntityIcon = ({ isCompany }: { isCompany: boolean }) => {
	const Icon = isCompany ? Building2 : User;

	return (
		<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border border-gray-200 dark:border-gray-600 flex-shrink-0">
			<Icon className="w-7 h-7 text-gray-400 dark:text-gray-400" />
		</div>
	);
};

export const CustomerDetailsHeader = ({
	customer,
	className,
	...actions
}: {
	customer: {
		customerId?: string;
		firstName?: string | null;
		lastName?: string | null;
		firmName?: string | null;
		email?: string | null;
		isCompany: boolean;
		isActive: boolean;
	};
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tC } = useTranslation('customers');
	const displayName = customer.isCompany
		? customer.firmName
		: [customer.firstName, customer.lastName].filter(Boolean).join(' ');
	const email = customer.email?.trim();
	const entityTypeName = customer.isCompany
		? tC('details.companyCustomer')
		: tC('details.individualCustomer');

	return (
		<EntityDetailsHeader
			icon={<CustomerEntityIcon isCompany={customer.isCompany} />}
			name={displayName || tC('details.unnamedCustomer')}
			statusBadge={<StatusBadge isActive={customer.isActive} />}
			subtitle={
				email ? (
					<a
						href={`mailto:${email}`}
						className="hover:underline break-all"
					>
						{email}
					</a>
				) : undefined
			}
			entityTypeName={entityTypeName}
			entityId={actions.entityId ?? customer.customerId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

const formatDate = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleString();
};

const formatAddressSummary = (
	address?: CustomerAddressRecord,
	t?: (key: string) => string
) => {
	if (!address?.address) {
		return t?.('details.linkedAddress') || 'Linked address';
	}

	const parts = [
		address.address.streetAddress,
		address.address.city,
		address.address.state,
		address.address.postalCode,
		address.address.country
	]
		.map(part => part?.trim())
		.filter((part): part is string => Boolean(part));

	if (parts.length === 0) {
		return t?.('details.linkedAddress') || 'Linked address';
	}

	return parts.join(', ');
};

const buildContactHref = (contact: CustomerContactRecord) => {
	const value = contact.contactValue?.trim();
	if (!value) {
		return null;
	}

	const typeName = contact.contactType?.name?.toLowerCase() ?? '';
	const isEmailType =
		typeName.includes('mail') || typeName.includes('email');
	const isPhoneType =
		typeName.includes('phone') ||
		typeName.includes('mobile') ||
		typeName.includes('tel') ||
		typeName.includes('telephone');

	if (isEmailType || (!typeName && value.includes('@'))) {
		return `mailto:${value}`;
	}

	if (isPhoneType || (!typeName && /^[+\d()\s.-]+$/u.test(value))) {
		const sanitizedValue = value.replace(/[^\d+]/g, '');
		return `tel:${sanitizedValue}`;
	}

	return null;
};

export const CustomerDetails = ({
	customerId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: CustomerDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tC } = useTranslation('customers');
	const [activeTab, setActiveTab] = useState('details');
	const { data, isLoading, isError, error } = useOne<CustomerRecord>({
		resource: 'customers',
		id: customerId
	});

	const customer = data?.data ?? null;

	const activeContacts = useMemo(() => {
		return (
			customer?.customerContacts?.filter(
				contact => !contact?.isDeleted
			) ?? []
		);
	}, [customer?.customerContacts]);

	const activeBankDetails = useMemo(() => {
		return (
			customer?.bankDetails?.filter(
				detail => !detail.isDeleted && detail.isActive
			) ?? []
		);
	}, [customer?.bankDetails]);

	return (
		<DelayedSkeleton
			isLoading={isLoading}
			fallback={<DetailsWithListsSkeleton sections={3} listCount={2} />}
		>
			{(() => {
				if (isError) {
					return (
						<div className="space-y-2">
							<p className="text-base font-medium text-destructive">
								{tC('details.unableToLoad')}
							</p>
							<p className="text-base text-gray-500 dark:text-slate-400">
								{error?.message ?? tC('details.unexpectedError')}
							</p>
						</div>
					);
				}

				if (!customer) {
					return (
						<p className="text-base text-gray-500 dark:text-slate-400">
							{tC('details.selectCustomer')}
						</p>
					);
				}

				return (
					<div className="flex flex-col h-full">
						{/* Header - shown when in modal mode */}
						{showHeader && (
							<CustomerDetailsHeader
								customer={customer}
								entityId={customerId}
								onEdit={onEdit}
								onClone={onClone}
								onDelete={onDelete}
								onOpenInNewWindow={onOpenInNewWindow}
								onClose={onClose}
								onBack={onBack}
								showBackButton={showBackButton}
								showCloseButton={!!onClose}
								className="pb-4 mb-4"
							/>
						)}

					<Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full min-h-0">
						{/* Tab Bar - fixed height at top */}
						<div className="flex-shrink-0 pb-2">
							<TabsList>
								<TabLink value="details" icon={FileText} label={t('tabs.details')} />
								<TabLink value="contracts" icon={ScrollText} label={t('tabs.contracts')} />
								<TabLink value="parkingSessions" icon={Car} label={t('tabs.parkingSessions')} />
								<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
							</TabsList>
						</div>

						{/* Tab Content - scrollable area with min-height for stable modal sizing */}
						<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
							<TabsContent value="details" className="space-y-6 mt-0">
						{/* Overview Section */}
						<DetailSection noBorderTop>
							<DetailFieldsGrid>
								{customer.dateOfBirth && (
									<DetailField
										label={tC('details.dateOfBirth')}
										value={formatDate(customer.dateOfBirth)}
									/>
								)}
								{customer.gender && (
									<DetailField
										label={tC('details.gender')}
										value={customer.gender}
									/>
								)}
								{customer.nationality && (
									<DetailField
										label={tC('details.nationality')}
										value={customer.nationality}
									/>
								)}
								{customer.idType && customer.idNumber && (
									<DetailField
										label={customer.idType}
										value={customer.idNumber}
									/>
								)}
								{customer.companyRegistrationNumber && (
									<DetailField
										label={tC('details.registration')}
										value={
											customer.companyRegistrationNumber
										}
									/>
								)}
								{customer.industry && (
									<DetailField
										label={tC('details.industry')}
										value={customer.industry}
									/>
								)}
								{customer.companySize && (
									<DetailField
										label={tC('details.companySize')}
										value={customer.companySize}
									/>
							)}
						{customer.preferredLanguage && (
							<DetailField
								label={tC('details.preferredLanguage')}
								value={
									customer.preferredLanguage === 'EN'
										? tC('fields.preferredLanguageEN')
										: tC('fields.preferredLanguageEL')
								}
							/>
						)}
							</DetailFieldsGrid>
						</DetailSection>

						{/* Fiscal Section */}
						{(customer.fiscalNumber || customer.taxOffice || customer.billingLegalName || customer.billingTradeName || customer.billingCountryCode) && (
						<DetailSection title={tC('details.fiscal')} collapsible>
							<DetailFieldsGrid>
								{customer.fiscalNumber && (
									<DetailField
										label={tC('details.fiscalNumber')}
										value={customer.fiscalNumber}
									/>
								)}
								{customer.taxOffice && (
									<DetailField
										label={tC('details.taxOffice')}
										value={customer.taxOffice}
									/>
								)}
								{customer.billingLegalName && (
									<DetailField
										label={tC('details.billingLegalName')}
										value={customer.billingLegalName}
									/>
								)}
								{customer.billingTradeName && (
									<DetailField
										label={tC('details.billingTradeName')}
										value={customer.billingTradeName}
									/>
								)}
								{customer.billingCountryCode && (
									<DetailField
										label={tC('details.billingCountryCode')}
										value={customer.billingCountryCode}
									/>
								)}
							</DetailFieldsGrid>
						</DetailSection>
						)}

						{activeContacts.length > 0 && (
							<DetailSection title={tC('details.contactMethods')} collapsible rowCount={activeContacts.length}>
								<div className="text-sm">
									{activeContacts.map(contact => {
										const href = buildContactHref(contact);
										const contactLabel = contact.contactType?.name ?? tC('details.contact');
										const labelWithCustom =
											contact.label
												? `${contactLabel} (${contact.label})`
												: contactLabel;
										const verifiedDate = formatDate(contact.verifiedAt);
										const verifiedText =
											contact.isVerified && verifiedDate
												? `${tC('details.verifiedOn')} ${verifiedDate}`
												: contact.isVerified
													? tC('details.verified')
													: null;

										return (
											<div
												key={contact.customerContactId}
												className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
											>
												<CustomerContactDisplayItem
													label={labelWithCustom}
													iconHint={contact.contactType?.name}
													value={contact.contactValue}
													href={href}
													isPrimary={contact.isPrimary}
													primaryLabel={tC('details.primary')}
													isVerified={contact.isVerified}
													verifiedText={verifiedText}
													note={contact.notes}
												/>
											</div>
										);
									})}
								</div>
							</DetailSection>
						)}

						{customer.customerAddresses?.length ? (
							<DetailSection title={tC('details.addresses')} collapsible rowCount={customer.customerAddresses?.length ?? 0}>
								<div className="text-sm">
									{customer.customerAddresses.map(address => (
										<div
											key={address.customerAddressId}
											className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
										>
											<CustomerAddressDisplayItem
												typeLabel={
													address.addressType?.name ??
													tC('details.address')
												}
												summary={formatAddressSummary(address, tC)}
												fallbackTypeLabel={tC('details.address')}
												fallbackSummary={tC('details.linkedAddress')}
											/>
										</div>
									))}
								</div>
							</DetailSection>
						) : null}

						{activeBankDetails.length > 0 && (
							<DetailSection title={tC('details.bankDetails')} collapsible rowCount={activeBankDetails.length}>
								<div className="space-y-4 text-sm">
									{activeBankDetails.map(detail => (
										<div
											key={detail.bankDetailsId}
											className="flex flex-col gap-2 p-4 border border-gray-200 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 dark:border-gray-700"
										>
											<div className="flex items-center justify-between gap-2">
												<span className="text-sm font-semibold tracking-wide text-gray-700 uppercase dark:text-slate-200">
													{detail.bankName ??
														t(
															'details.bankAccount'
														)}
												</span>
												{detail.isPrimary ? (
													<Badge variant="secondary">
														{tC('details.primary')}
													</Badge>
												) : null}
											</div>
											<DetailFieldsGrid>
												{detail.accountHolderName ? (
													<span>
														{tC(
															'details.accountHolder'
														)}
														:{' '}
														{
															detail.accountHolderName
														}
													</span>
												) : null}
												{detail.accountNumber ? (
													<span>
														{tC(
															'details.accountNumber'
														)}
														: {detail.accountNumber}
													</span>
												) : null}
												{detail.accountType ? (
													<span>
														{tC(
															'details.accountType'
														)}
														: {detail.accountType}
													</span>
												) : null}
												{detail.iban ? (
													<span>
														IBAN: {detail.iban}
													</span>
												) : null}
												{detail.bankCode ? (
													<span>
														{tC('details.bankCode')}:{' '}
														{detail.bankCode}
													</span>
												) : null}
												{detail.branchCode ? (
													<span>
														{tC(
															'details.branchCode'
														)}
														: {detail.branchCode}
													</span>
												) : null}
												{detail.branchName ? (
													<span>
														{tC('details.branch')}:{' '}
														{detail.branchName}
													</span>
												) : null}
												{detail.currency ? (
													<span>
														{tC('details.currency')}:{' '}
														{detail.currency}
													</span>
												) : null}
												{detail.purpose ? (
													<span>
														{tC('details.purpose')}:{' '}
														{detail.purpose}
													</span>
												) : null}
												<div className="flex gap-2 mt-1">
													<StatusBadgeDisplay
														isActive={Boolean(
															detail.isActive
														)}
														activeLabel={tC(
															'details.active'
														)}
														inactiveLabel={tC(
															'details.inactive'
														)}
													/>
													<Badge
														variant={
															detail.isVerified
																? 'default'
																: 'outline'
														}
													>
														{detail.isVerified
															? tC(
																	'details.verified'
																)
															: tC(
																	'details.unverified'
																)}
													</Badge>
												</div>
												{detail.verificationMethod &&
												detail.verifiedAt ? (
													<span>
														{tC(
															'details.verifiedVia'
														)}{' '}
														{
															detail.verificationMethod
														}{' '}
														{tC('details.on')}{' '}
														{formatDate(
															detail.verifiedAt
														)}
													</span>
												) : null}
												{detail.notes ? (
													<span>
														{tC('details.notes')}:{' '}
														{detail.notes}
													</span>
												) : null}
											</DetailFieldsGrid>
										</div>
									))}
								</div>
							</DetailSection>
						)}

						{customer.customerBeneficiaries?.length ? (
							<DetailSection title={tC('details.beneficiaries')}>
								<div className="text-sm">
									{customer.customerBeneficiaries
										.filter(
											beneficiary =>
												!beneficiary.isDeleted
										)
										.map(beneficiary => {
											const beneficiaryIsCompany =
												beneficiary.beneficiary?.isCompany;

											const beneficiaryName =
												beneficiary.beneficiary?.displayName ??
												(beneficiary.beneficiary
													? (beneficiary.beneficiary
															.isCompany
															? beneficiary
																	.beneficiary
																	.firmName
															: `${beneficiary.beneficiary.firstName || ''} ${beneficiary.beneficiary.lastName || ''}`.trim()) ||
														t(
															'details.unnamedBeneficiary'
														)
													: tC('details.beneficiary'));

											return (
												<div
													key={
														beneficiary.customerBeneficiaryId
													}
													className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
												>
													<CustomerDisplayItem
														customerId={beneficiary.beneficiaryId}
														displayName={beneficiaryName}
														isCompany={beneficiaryIsCompany}
														customerTypeName={
															typeof beneficiaryIsCompany ===
															'boolean'
																? beneficiaryIsCompany
																	? tC(
																			'details.business'
																		)
																	: tC(
																			'details.individual'
																		)
																: undefined
														}
														isActive={beneficiary.isActive}
														inactiveLabel={tC('details.inactive')}
														tags={
															beneficiary.relationshipType
																? [beneficiary.relationshipType]
																: []
														}
														note={beneficiary.notes}
														singleLine
													/>
												</div>
											);
										})}
								</div>
							</DetailSection>
						) : null}

						{customer.vehicles?.length ? (
							<DetailSection title={tC('details.vehicles')} collapsible rowCount={customer.vehicles?.length ?? 0}>
								<VehicleDisplayList>
									{customer.vehicles.map(vehicle => (
											<VehicleDisplayItem
												key={vehicle.vehicleId}
												vehicleId={vehicle.vehicleId}
												plateNumber={vehicle.plateNumber}
												vehicleTypeName={vehicle.vehicleType?.name}
											make={vehicle.make}
											model={vehicle.model}
											color={vehicle.color}
											isActive={vehicle.isActive}
											inactiveLabel={tC('details.inactive')}
										className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
									/>
									))}
								</VehicleDisplayList>
							</DetailSection>
						) : null}

						{customer.notes && (
							<DetailSection title={tC('details.notes')}>
								<p className="text-sm leading-6 text-gray-500 whitespace-pre-wrap dark:text-slate-500">
									{customer.notes}
								</p>
							</DetailSection>
						)}
							</TabsContent>

							<TabsContent value="contracts" className="mt-0">
								<CustomerContractsTab
									customerId={customerId}
									isActive={activeTab === 'contracts'}
								/>
							</TabsContent>

							<TabsContent value="parkingSessions" className="mt-0">
								<ParkingSessionsListPanel
									customerId={customerId}
									isActive={activeTab === 'parkingSessions'}
								/>
							</TabsContent>

							<TabsContent value="audit" className="mt-0">
								<AuditTab
									data={{
										createdAt: customer.createdAt,
										updatedAt: customer.updatedAt,
										activatedAt: customer.activatedAt,
										deactivatedAt: customer.deactivatedAt
									}}
								/>
							</TabsContent>
						</div>
					</Tabs>
					</div>
				);
			})()}
		</DelayedSkeleton>
	);
};
